import { observer } from 'mobx-react';
import cls from 'classnames';
import { isNumeric } from '@cutwise/utils';

import Icon from '@/common/components/Icon/Icon';
import priceFormatter from '@/common/helpers/priceFormatter';
import { MEDIA_SIZE_LARGE, MEDIA_SIZE_MEDIUM } from '@/media/constants/sizeCodes';
import Diamond from '@/product/entity/Diamond';
import { ProductEntity } from '@/product/types';

import styles from './ProductPrice.module.css';

interface ProductPriceProps {
  product: ProductEntity;
  mediaSize?: Nullable<string>;
  'aria-label'?: string;
}

function ProductPrice(props: ProductPriceProps) {
  const { product, mediaSize, 'aria-label': ariaLabel } = props;

  const isMedium = mediaSize === MEDIA_SIZE_MEDIUM;
  const isLarge = mediaSize === MEDIA_SIZE_LARGE;
  const fcrfGrade = product instanceof Diamond ? product.fcrfGrade : null;
  const fcrfPrice = fcrfGrade?.averagePrice || null;
  const fcrfPriceCt = fcrfGrade?.rangePrice || null;

  return (
    <div
      className={cls(styles.productPrice, {
        [styles.productPriceLarge]: isLarge,
        [styles.productPriceMedium]: isMedium,
      })}
      aria-label={ariaLabel}
    >
      {product.price && (
        <div className={styles.productPriceItem}>
          <div className={styles.productPriceItemLabel}>
            <Icon name="user-billing-usd" className={styles.productPriceItemIcon} />

            <span>Price</span>
          </div>

          <div className={styles.productPriceValue}>
            {isNumeric(product.priceCt) && (
              <div className={styles.productPriceCt}>
                <span aria-label="Product price per carat">{priceFormatter.format(+product.priceCt.toFixed(0))}</span>

                <span>/ct</span>
              </div>
            )}

            <div className={styles.productPriceAbs} aria-label="Product price">${product.price.toFixed(0)}</div>
          </div>
        </div>
      )}

      {fcrfPrice && (
        <div className={styles.productPriceItem}>
          <div className={styles.productPriceItemLabel}>
            <Icon name="user-billing-usd" className={styles.productPriceItemIcon} />

            <span>FCRF Price</span>
          </div>

          <div className={styles.productPriceValue}>
            {fcrfPriceCt && (
              <div className={styles.productPriceCt}>
                <span aria-label="FCRF price per carat">{fcrfPriceCt}</span>

                <span>/ct</span>
              </div>
            )}

            <div className={styles.productPriceAbs} aria-label="FCRF price">{fcrfPrice}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default observer(ProductPrice);
